// Shared list-page pagination bar. Client-side paging: the page owns the
// full (filtered, sorted) row array and slices it with usePagination; this
// bar renders the "Showing X–Y of Z" summary, page-size select, and
// prev/next controls. Hidden entirely when everything fits on one page at
// the smallest page size.

import { useState } from 'react';

export const PAGE_SIZES = [25, 50, 100];

// Hook: returns the current slice + the props Pagination needs.
export function usePagination(rows, initialSize = 50) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, pageCount);   // clamp when filters shrink the set
  const paged = rows.slice((curPage - 1) * pageSize, curPage * pageSize);
  return {
    paged,
    pagination: {
      total, page: curPage, pageCount, pageSize,
      onPage: setPage,
      onPageSize: (n) => { setPageSize(n); setPage(1); },
    },
  };
}

export default function Pagination({ total, page, pageCount, pageSize, onPage, onPageSize }) {
  if (total <= PAGE_SIZES[0]) return null;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-2 text-sm text-gray-600">
      <span>
        Showing <span className="font-medium">{from}–{to}</span> of <span className="font-medium">{total}</span>
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          Per page
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Prev
          </button>
          <span className="px-2 text-xs text-gray-500">{page} / {pageCount}</span>
          <button
            onClick={() => onPage(page + 1)}
            disabled={page >= pageCount}
            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
